import React, { useState, useEffect } from 'react';
import { circuitConfig as config, CircuitDefs } from './circuitConfig';

/**
 * DatexTraderCircuit - Technical Order Routing Schematic
 * 
 * Detailed technical blueprint showing order flow architecture.
 * Grid-based, labeled, engineering-grade visualization.
 */

export function DatexTraderCircuit({ className = '' }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), config.animation.fadeIn);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div 
      className={`relative ${className} transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      style={{ width: '260px', height: '180px' }}
    >
      <svg 
        viewBox="0 0 130 90" 
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter id="heroGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          {/* Grid pattern */}
          <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(15, 118, 110, 0.08)" strokeWidth="0.3"/>
          </pattern>
        </defs>

        {/* Background grid */}
        <rect width="130" height="90" fill="url(#grid)" />

        {/* Connection paths - Technical routing */}
        <g className="connections">
          {/* Main horizontal bus */}
          <line x1="10" y1="45" x2="120" y2="45" stroke="rgba(15, 118, 110, 0.15)" strokeWidth="0.4" />
          
          {/* Input to OMS */}
          <path d="M 15 45 L 15 35 L 35 35" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          
          {/* OMS to Router */}
          <path d="M 45 35 L 55 35 L 55 45 L 65 45" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          
          {/* Router to Venues (fan-out) */}
          <path d="M 80 45 L 90 45 L 90 25 L 105 25" fill="none" stroke="rgba(15, 118, 110, 0.35)" strokeWidth="0.5" />
          <path d="M 80 45 L 115 45" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <path d="M 80 45 L 90 45 L 90 65 L 105 65" fill="none" stroke="rgba(15, 118, 110, 0.35)" strokeWidth="0.5" />
          
          {/* Feedback loop (dashed) */}
          <path d="M 72 52 L 72 72 L 40 72 L 40 42" fill="none" stroke="rgba(15, 118, 110, 0.25)" strokeWidth="0.4" strokeDasharray="2,2" />
        </g>

        {/* Data pulse */}
        <g className="data-pulse">
          <circle r="1.5" fill="#0F766E" filter="url(#heroGlow)">
            <animateMotion dur="6s" repeatCount="indefinite">
              <mpath href="#mainPath" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;1;0" dur="6s" repeatCount="indefinite" />
          </circle>
          <path id="mainPath" d="M 15 35 L 40 35 L 55 35 L 55 45 L 72 45 L 115 45" fill="none" stroke="none" />
        </g>

        {/* Nodes with labels */}
        <g className="nodes" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {/* API Input */}
          <rect x="10" y="30" width="10" height="10" rx="1.5" fill="rgba(15, 118, 110, 0.08)" stroke="rgba(15, 118, 110, 0.5)" strokeWidth="0.8" />
          <text x="15" y="27" textAnchor="middle" fill="#0F766E" fontSize="4" fontWeight="500">API</text>
          
          {/* OMS */}
          <rect x="35" y="30" width="14" height="10" rx="1.5" fill="rgba(15, 118, 110, 0.1)" stroke="rgba(15, 118, 110, 0.6)" strokeWidth="0.8" />
          <text x="42" y="37" textAnchor="middle" fill="#0F766E" fontSize="4.5" fontWeight="500">OMS</text>
          
          {/* Smart Router (Core) */}
          <rect x="65" y="38" width="18" height="14" rx="2" fill="rgba(15, 118, 110, 0.15)" stroke="#0F766E" strokeWidth="1" />
          <text x="74" y="47" textAnchor="middle" fill="#0F766E" fontSize="5" fontWeight="600">ROUTER</text>
          
          {/* Venue A */}
          <rect x="105" y="21" width="12" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.06)" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <text x="111" y="27" textAnchor="middle" fill="#0F766E" fontSize="3.5">BNB</text>
          
          {/* Venue B (Primary) */}
          <rect x="115" y="41" width="12" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.1)" stroke="rgba(15, 118, 110, 0.6)" strokeWidth="0.8" />
          <text x="121" y="47" textAnchor="middle" fill="#0F766E" fontSize="3.5" fontWeight="500">OKX</text>
          
          {/* Venue C */}
          <rect x="105" y="61" width="12" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.06)" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <text x="111" y="67" textAnchor="middle" fill="#0F766E" fontSize="3.5">KRK</text>
        </g>

        {/* Technical annotations */}
        <g className="annotations" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {/* Latency indicator */}
          <text x="74" y="58" textAnchor="middle" fill="#0F766E" fontSize="3.5" fontWeight="500">p99 &lt;2ms</text>
          
          {/* Feedback label */}
          <text x="56" y="76" textAnchor="middle" fill="rgba(15, 118, 110, 0.6)" fontSize="3">fill_ack</text>
        </g>

        {/* Status indicator */}
        <g className="status">
          <circle cx="6" cy="6" r="2.5" fill="#10B981">
            <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
          </circle>
          <text x="12" y="8" fill="#10B981" fontSize="3.5" fontWeight="600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>LIVE</text>
        </g>
      </svg>
    </div>
  );
}

export default DatexTraderCircuit;
